import { SimNPC, isCardForbidden } from '../types/SimNPC';
import { TileType, getAttributeFromTile } from '../types/GameMap';
import { SimWorld, addLog, isGroupModeEnabled, isAsymmetricEvolutionEnabled } from '../types/SimWorld';
import { getAvailableCards, CardPoolConfig } from '../utils/CardFilter';
import type { ExtendedCardData } from '../../utils/cardManager';
import type { CardWithId } from '../../types/card';

/**
 * 突然変異システム定数
 */
export const MUTATION_CONSTANTS = {
  MUTATION_CHANCE: 0.05, // 基本突然変異率（5%/日）
  ATTRIBUTE_CARD_CHANCE: 0.8, // 属性カード獲得率（80%）
  RANDOM_CARD_CHANCE: 0.2, // ランダムカード獲得率（20%）
  MIN_GAMES_FOR_EVALUATION: 5, // 評価に必要な最低試合数
  MIN_DAYS_FOR_MUTATION: 20, // 突然変異発生に必要な最低生存日数
  MAX_DAYS_FOR_MUTATION: 50, // 突然変異発生の上限日数（これ以上は統計収集期間）
};

/**
 * NPCの有効なカードプール設定を取得
 * グループモードならグループのカードプール、それ以外はワールドのカードプールを使用
 */
function getEffectiveCardPoolConfig(npc: SimNPC, world: SimWorld): CardPoolConfig | undefined {
  if (isGroupModeEnabled(world) && npc.group && world.groupModeConfig) {
    const groupConfig = world.groupModeConfig.groups.find(g => g.id === npc.group);
    if (groupConfig) {
      return groupConfig.cardPoolConfig;
    }
  }
  return world.cardPoolConfig;
}

/**
 * NPCのデッキに突然変異を適用
 * 属性ゾーンにいる場合、確率でその属性のカードを獲得し、
 * 使用率・勝率の低いカードと入れ替える
 * ※同種カード間での変異（モンスター→モンスター、魔法→魔法、フィールド→フィールド）
 */
export function processMutation(
  npc: SimNPC,
  tileType: TileType,
  allCards: ExtendedCardData[],
  world: SimWorld
): void {
  // 属性ゾーンかどうかチェック
  const attribute = getAttributeFromTile(tileType);
  if (!attribute) {
    return; // 属性ゾーン以外は何もしない
  }

  // 生存日数チェック（20日以上50日以下で突然変異可能）
  if (npc.daysLived < MUTATION_CONSTANTS.MIN_DAYS_FOR_MUTATION) {
    return; // まだ若すぎる
  }
  if (npc.daysLived > MUTATION_CONSTANTS.MAX_DAYS_FOR_MUTATION) {
    return; // 統計収集期間に入った
  }

  // 突然変異発生チェック
  if (Math.random() >= MUTATION_CONSTANTS.MUTATION_CHANCE) {
    return; // 突然変異は発生しなかった
  }

  // 非対称進化モードではrecordedCardStatsを使用
  const useRecordedStats = isAsymmetricEvolutionEnabled(world);

  // デッキから入れ替えるカードを選択（成績の悪いカードを優先）
  const replaceIndex = selectWeakestCardIndex(npc, useRecordedStats);
  const oldCard = npc.deck[replaceIndex];

  // デッキ内の禁忌カード数をチェック
  const forbiddenCount = npc.deck.filter((c) => isCardForbidden(c)).length;
  const allowForbidden = forbiddenCount === 0; // 0枚なら禁忌カード許可

  // 新しいカードを決定（入れ替え対象と同じカードタイプから選択）
  // NPCのグループに応じたカードプール設定を適用
  const cardPoolConfig = getEffectiveCardPoolConfig(npc, world);
  const availableCards = getAvailableCards(allCards, cardPoolConfig);
  const newCard = selectMutationCard(attribute, oldCard.type, availableCards, allowForbidden);
  if (!newCard) {
    return; // 適切なカードが見つからなかった
  }

  // カードを入れ替え
  npc.deck[replaceIndex] = {
    ...newCard,
    uniqueId: `${newCard.id}_mutation_${Date.now()}_${Math.random()}`,
  };

  // ログ出力（非対称進化モードでは記録対象の統計を使用）
  const cardStats = useRecordedStats ? npc.recordedCardStats : npc.cardStats;
  const stat = cardStats.get(oldCard.id);
  const statsLabel = useRecordedStats ? '記録対象' : '';
  const statsText = stat && stat.gamesInDeck >= MUTATION_CONSTANTS.MIN_GAMES_FOR_EVALUATION
    ? (() => {
        const usageRate = ((stat.gamesWithCard / stat.gamesInDeck) * 100).toFixed(0);
        const winRate = stat.gamesWithCard > 0
          ? ((stat.winsWithCard / stat.gamesWithCard) * 100).toFixed(0)
          : '0';
        return `${statsLabel}使用率${usageRate}%/勝率${winRate}%`;
      })()
    : '未評価';
  addLog(
    world,
    `🧬 ${npc.name}のデッキに突然変異発生！${oldCard.name}(${statsText}) → ${newCard.name}（${attribute}の力）`
  );
}

/**
 * 成績の悪いカードのインデックスを選択
 * - 使用率（実際に使用した回数/デッキに入っていた試合数）と勝率を考慮
 * - 使用率が低い（手札腐りしやすい）カードを優先的に入れ替え対象に
 * - 条件を満たすカードがなければランダム選択
 * @param npc 対象NPC
 * @param useRecordedStats 非対称進化モードでrecordedCardStatsを使用するかどうか
 */
function selectWeakestCardIndex(npc: SimNPC, useRecordedStats: boolean = false): number {
  const { deck } = npc;
  const cardStats = useRecordedStats ? npc.recordedCardStats : npc.cardStats;
  const minGames = MUTATION_CONSTANTS.MIN_GAMES_FOR_EVALUATION;

  // 各カードのスコアを計算（低いほど弱い）
  const cardScores: { index: number; score: number; hasStats: boolean }[] = [];

  for (let i = 0; i < deck.length; i++) {
    const card = deck[i];
    const stat = cardStats.get(card.id);

    // gamesInDeck が一定以上（デッキに入った状態で試合した回数）
    if (stat && stat.gamesInDeck >= minGames) {
      // 使用率 = 実際に使用した回数 / デッキに入っていた試合数
      const usageRate = stat.gamesWithCard / stat.gamesInDeck;
      // 勝率 = 使用して勝った回数 / 使用した試合数（使用していない場合は0）
      const winRate = stat.gamesWithCard > 0 ? stat.winsWithCard / stat.gamesWithCard : 0;
      // 総合スコア = 使用率60% + 勝率40%（使用率をより重視）
      const score = usageRate * 0.6 + winRate * 0.4;
      cardScores.push({ index: i, score, hasStats: true });
    } else {
      // 統計不足のカードは高スコアとして扱う（保護）
      cardScores.push({ index: i, score: 1.0, hasStats: false });
    }
  }

  // 統計のあるカードをスコア順（低い順）にソート
  const evaluatedCards = cardScores
    .filter((c) => c.hasStats)
    .sort((a, b) => a.score - b.score);

  if (evaluatedCards.length > 0) {
    // 最も弱いカード（使用率・勝率が低い）を選択
    // 同率の場合はその中からランダム
    const lowestScore = evaluatedCards[0].score;
    const weakestCards = evaluatedCards.filter((c) => c.score === lowestScore);
    const selected = weakestCards[Math.floor(Math.random() * weakestCards.length)];
    return selected.index;
  }

  // 評価済みカードがなければランダム選択
  return Math.floor(Math.random() * deck.length);
}

/**
 * 突然変異で獲得するカードを選択
 * @param attribute 属性
 * @param cardType カードタイプ（monster, magic, field）- 入れ替え対象と同じタイプのみ選択
 * @param cards 利用可能なカードリスト（フィルタリング済み）
 * @param allowForbidden 禁忌カードを許可するかどうか
 */
function selectMutationCard(
  attribute: string,
  cardType: string,
  cards: ExtendedCardData[],
  allowForbidden: boolean
): ExtendedCardData | null {
  // 同じカードタイプのみを対象とする（禁忌制限を考慮）
  const sameTypeCards = cards.filter(
    (c) => c.type === cardType && (allowForbidden || !isCardForbidden(c))
  );

  if (sameTypeCards.length === 0) {
    return null;
  }

  // 80%の確率で属性一致カード、20%でランダム
  if (Math.random() < MUTATION_CONSTANTS.ATTRIBUTE_CARD_CHANCE) {
    // 属性一致カードから選択
    const attributeCards = sameTypeCards.filter((c) => c.attribute === attribute);
    if (attributeCards.length > 0) {
      return attributeCards[Math.floor(Math.random() * attributeCards.length)];
    }
  }

  // ランダムカード選択（同タイプ内）
  return sameTypeCards[Math.floor(Math.random() * sameTypeCards.length)];
}

/**
 * 突然変異ログ用：属性ゾーン名を取得
 */
export function getAttributeZoneName(attribute: string): string {
  switch (attribute) {
    case '炎':
      return '炎の領域';
    case '水':
      return '水の領域';
    case '光':
      return '光の領域';
    case '闇':
      return '闇の領域';
    case '原始':
      return '原始の領域';
    case '未来':
      return '未来の領域';
    default:
      return '不明な領域';
  }
}

/**
 * 禁忌の地での禁忌カード獲得処理
 * - デッキに禁忌カードがない場合のみ発動
 * - 他のNPCが持っていない禁忌カードを優先
 * - 勝率の低いカードと入れ替え
 */
export function processForbiddenZone(
  npc: SimNPC,
  allCards: ExtendedCardData[],
  world: SimWorld
): void {
  // デッキに禁忌カードがあるかチェック
  const hasForbidden = npc.deck.some((c) => isCardForbidden(c));
  if (hasForbidden) {
    return; // 既に禁忌カードを持っている
  }

  // 生存日数チェック（突然変異と同じ条件：20日以上50日以下）
  if (npc.daysLived < MUTATION_CONSTANTS.MIN_DAYS_FOR_MUTATION) {
    return;
  }
  if (npc.daysLived > MUTATION_CONSTANTS.MAX_DAYS_FOR_MUTATION) {
    return;
  }

  // 発生確率チェック（突然変異と同じ5%）
  if (Math.random() >= MUTATION_CONSTANTS.MUTATION_CHANCE) {
    return;
  }

  // NPCのグループに応じたカードプール設定を適用して利用可能なカードを取得
  const cardPoolConfig = getEffectiveCardPoolConfig(npc, world);
  const availableCards = getAvailableCards(allCards, cardPoolConfig);

  // 利用可能な禁忌カードを取得
  const allForbiddenCards = availableCards.filter((c) => isCardForbidden(c));
  if (allForbiddenCards.length === 0) {
    return;
  }

  // 世界中のNPCが持っている禁忌カードIDを収集
  const ownedForbiddenIds = new Set<string>();
  for (const otherNpc of world.npcs) {
    if (!otherNpc.isAlive) continue;
    for (const card of otherNpc.deck) {
      if (isCardForbidden(card)) {
        ownedForbiddenIds.add(card.id);
      }
    }
  }

  // 未所持の禁忌カードをフィルタ
  const availableForbiddenCards = allForbiddenCards.filter(
    (c) => !ownedForbiddenIds.has(c.id)
  );

  // 未所持の禁忌カードがなければ変換しない
  if (availableForbiddenCards.length === 0) {
    return;
  }

  // ランダムに1枚選択
  const forbiddenCard =
    availableForbiddenCards[Math.floor(Math.random() * availableForbiddenCards.length)];

  // 非対称進化モードではrecordedCardStatsを使用
  const useRecordedStats = isAsymmetricEvolutionEnabled(world);

  // 入れ替えるカードを選択（勝率の低いカード）
  const replaceIndex = selectWeakestCardIndex(npc, useRecordedStats);
  const oldCard = npc.deck[replaceIndex];

  // カードを入れ替え
  npc.deck[replaceIndex] = {
    ...forbiddenCard,
    uniqueId: `${forbiddenCard.id}_forbidden_${Date.now()}_${Math.random()}`,
  };

  // ログ出力（非対称進化モードでは記録対象の統計を使用）
  const cardStats = useRecordedStats ? npc.recordedCardStats : npc.cardStats;
  const stat = cardStats.get(oldCard.id);
  const statsLabel = useRecordedStats ? '記録対象' : '';
  const statsText = stat && stat.gamesInDeck >= MUTATION_CONSTANTS.MIN_GAMES_FOR_EVALUATION
    ? (() => {
        const usageRate = ((stat.gamesWithCard / stat.gamesInDeck) * 100).toFixed(0);
        const winRate = stat.gamesWithCard > 0
          ? ((stat.winsWithCard / stat.gamesWithCard) * 100).toFixed(0)
          : '0';
        return `${statsLabel}使用率${usageRate}%/勝率${winRate}%`;
      })()
    : '未評価';
  addLog(
    world,
    `⚔️ ${npc.name}が禁忌の地で禁忌カードを獲得！${oldCard.name}(${statsText}) → ${forbiddenCard.name}【禁忌】`
  );
}
